import React, { useEffect, useState } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import request from '../utils/fetcher'

/**
 * A filter box that offers the values already out there as you type.
 *
 * The suggestions are whole values, so picking one filters on exactly that rather than on a
 * fragment of it. Typing something nobody has is still allowed — the box filters on what was
 * typed, and an empty list of suggestions is not an error.
 *
 * Only the fields the server will answer for get this; `suggest` is the name it knows them by.
 * The lookup is debounced and the answer is dropped if another keystroke overtook it, so a slow
 * reply cannot repopulate the list under a later one.
 */
const DEBOUNCE_MS = 250

export default function SuggestingField({
  label,
  field,
  value,
  onChange,
  // A menu that opens for typing wants the caret; a field standing among others in a form does
  // not, or the last one on the page would take the focus from the first.
  autoFocus = true,
}) {
  const [typed, setTyped] = useState(value ?? '')
  const [options, setOptions] = useState([])

  // the value can change from outside — a pill cleared, a filter reset
  useEffect(() => {
    setTyped(value ?? '')
  }, [value])

  useEffect(() => {
    const wanted = typed.trim()
    if (!wanted) {
      setOptions([])
      return undefined
    }
    let current = true
    const timer = setTimeout(() => {
      request('GET', '/carrier/location-control/suggestions', {
        field,
        q: wanted,
      })
        .then((answer) => {
          if (current) setOptions(answer?.values ?? [])
        })
        // a filter box that cannot suggest is still a filter box
        .catch(() => {
          if (current) setOptions([])
        })
    }, DEBOUNCE_MS)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [typed, field])

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      size="small"
      options={options}
      // The text is the only thing held here. Controlling the selected value as well would make
      // MUI keep the two in step by resetting the text whenever they differ, which is every
      // keystroke before the filter has caught up — and the box would lose what was typed.
      inputValue={typed}
      // the server has already decided which values contain what was typed
      filterOptions={(all) => all}
      onInputChange={(_, next) => {
        setTyped(next)
        onChange(next || undefined)
      }}
      onChange={(_, picked) => {
        setTyped(picked || '')
        onChange(picked || undefined)
      }}
      renderInput={(params) => (
        <TextField {...params} fullWidth autoFocus={autoFocus} label={label} />
      )}
    />
  )
}
